import { useEffect } from 'react';
import { toast } from 'sonner';
import { apiBaseUrl } from './portal';

/**
 * Update-available toast. The backend reports its build via GET /api/version
 * ({version, sha, builtAt, notes:[{type,text}]}). The client remembers the
 * identity it saw at boot; a later poll reporting a different identity means a
 * deploy happened. Port of GIS-EYE src/updateToast.js.
 */

export const POLL_MS = 10 * 60 * 1000;
export const MIN_CHECK_GAP_MS = 60 * 1000;
export const SNOOZE_KEY = 'ultimate-gis:update-snooze';
export const BEFORE_UPDATE_EVENT = 'gis:before-update';
const MAX_NOTES = 4;
const TOAST_ID = 'update-available';

/** Stable identity of a build report; null when the report is unusable. */
export function buildIdentity(info) {
  if (!info || typeof info !== 'object') return null;
  return info.sha || info.builtAt || info.version || null;
}

/** Cap and clean the notes list for display. */
export function displayNotes(notes, max = MAX_NOTES) {
  if (!Array.isArray(notes)) return [];
  return notes
    .filter((n) => n && typeof n.text === 'string' && n.text.trim())
    .slice(0, max)
    .map((n) => ({
      type: typeof n.type === 'string' && n.type ? n.type : 'note',
      text: n.text.trim(),
    }));
}

async function fetchVersion() {
  try {
    const base = apiBaseUrl();
    const res = await fetch(`${base}/api/version`, { cache: 'no-store' });
    if (!res.ok) return null;
    const info = await res.json();
    return buildIdentity(info) ? info : null;
  } catch {
    return null;
  }
}

function readSnooze() {
  try { return localStorage.getItem(SNOOZE_KEY); } catch { return null; }
}
function writeSnooze(identity) {
  try { localStorage.setItem(SNOOZE_KEY, identity); } catch { /* re-offers next time */ }
}

export function applyUpdate() {
  window.dispatchEvent(new Event(BEFORE_UPDATE_EVENT));
  setTimeout(() => window.location.reload(), 100);
}

function UpdateBody({ notes }) {
  return (
    <div>
      <div style={{ fontWeight: 600 }}>Update available</div>
      {notes.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 16, fontSize: 12, listStyle: 'disc' }}>
          {notes.map((n, i) => (
            <li key={i}><strong>{n.type}</strong> {n.text}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function showUpdateToast(info) {
  const identity = buildIdentity(info);
  toast(<UpdateBody notes={displayNotes(info.notes)} />, {
    id: TOAST_ID,
    duration: Infinity,
    dismissible: false,
    action: { label: 'Update now', onClick: applyUpdate },
    cancel: {
      label: 'Later',
      onClick: () => { writeSnooze(identity); toast.dismiss(TOAST_ID); },
    },
  });
}

/** Mount once near the app root. Requires a sonner <Toaster /> to be rendered. */
export function useUpdateToast() {
  useEffect(() => {
    let baseline = null;
    let visible = false;
    let lastCheckAt = 0;
    let disposed = false;

    const check = async (force = false) => {
      const now = Date.now();
      if (!force && now - lastCheckAt < MIN_CHECK_GAP_MS) return;
      lastCheckAt = now;
      const info = await fetchVersion();
      if (disposed || !info) return;
      const identity = buildIdentity(info);
      if (!baseline) { baseline = identity; return; }
      if (identity === baseline || visible) return;
      if (readSnooze() === identity) return;
      visible = true;
      showUpdateToast(info);
    };

    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    const timer = setInterval(() => check(), POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    check(true);
    return () => {
      disposed = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}
